import { defineStore } from 'pinia'
import { collection, doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { LabAdminUser } from '@/globals/typescript/models/LabAdminUser'
import { i18n } from '@/locales'
import {
  LAB_ADMIN_LANGUAGES,
  type TLabAdminLocale,
} from '@team-milva/lab-models/language'

export const useUserStore = defineStore('user', {
  state: () => ({
    currentUser: null as LabAdminUser | null,
    allLabAdminUsers: [] as LabAdminUser[],
    menuCollapsed: localStorage.getItem('menu:collapsed') === 'true',

    unsubscribeCurrentUser: null as (() => void) | null,
    unsubscribeAllLabAdminUsers: null as (() => void) | null,
  }),

  actions: {
    getCurrentUser(userID: string): void {
      if (this.currentUserLoaded) {
        return
      }

      const unsubscribe = onSnapshot(
        doc(db, 'Apps', '-lab-admin-', 'Users', userID),
        (doc) => {
          const currentUser = new LabAdminUser(doc.data() as LabAdminUser)

          // Apply the admin's own persisted language on every load — the
          // only way a saved preference survives a fresh device/session,
          // since main.ts's boot sequence only ever consults localStorage
          // + browser detection before the user is known.
          const locale = LAB_ADMIN_LANGUAGES[currentUser.languageCode]?.locale
          if (locale && locale !== i18n.global.locale.value) {
            i18n.global.locale.value = locale as TLabAdminLocale
            localStorage.setItem('lang', locale)
          }

          this.currentUser = currentUser
          this.unsubscribeCurrentUser = unsubscribe
        }
      )
    },

    getAllLabAdminUsers(): Promise<LabAdminUser[]> {
      if (this.allLabAdminUsersLoaded) {
        return Promise.resolve(this.allLabAdminUsers)
      }

      return new Promise((resolve) => {
        let resolved = false
        const unsubscribe = onSnapshot(
          collection(db, 'Apps', '-lab-admin-', 'Users'),
          (querySnapshot) => {
            this.allLabAdminUsers = querySnapshot.docs.map(
              (doc) => new LabAdminUser(doc.data() as LabAdminUser)
            )
            this.unsubscribeAllLabAdminUsers = unsubscribe

            // Only resolve once
            if (!resolved) {
              resolved = true
              resolve(this.allLabAdminUsers)
            }
          }
        )
      })
    },
    resetStore(): void {
      this.currentUser = null
      this.allLabAdminUsers = []

      if (this.unsubscribeCurrentUser) {
        this.unsubscribeCurrentUser()
        this.unsubscribeCurrentUser = null
      }
      if (this.unsubscribeAllLabAdminUsers) {
        this.unsubscribeAllLabAdminUsers()
        this.unsubscribeAllLabAdminUsers = null
      }
    },

    toggleMenuCollapsed(): void {
      this.menuCollapsed = !this.menuCollapsed
      localStorage.setItem('menu:collapsed', String(this.menuCollapsed))
    },

    updateUserDoc(userID: string, values: Partial<LabAdminUser>): void {
      const userRef = doc(db, 'Apps', '-lab-admin-', 'Users', userID)
      updateDoc(userRef, { ...values })
    },

    updateLastSeen(): void {
      if (!this.currentUser) {
        return
      }

      // Only update if more than x minutes have passed since last update
      const now = Date.now()
      const interval = 5 * 60 * 1000 // 5 minutes in milliseconds
      const lastSeen = this.currentUser.timestamps.lastSeenAt

      if (now - lastSeen < interval) {
        return
      }

      this.updateUserDoc(this.currentUser.id, {
        timestamps: {
          ...this.currentUser.timestamps, // TODO: Is required to avoid overwriting other timestamp fields
          lastSeenAt: now,
        },
      })
    },
    canUser() {
      return this.currentUser?.canUser.bind(this.currentUser)
    },
  },

  getters: {
    // Loaders
    currentUserLoaded: (state) => {
      return !!state.unsubscribeCurrentUser
    },
    allLabAdminUsersLoaded: (state) => {
      return !!state.unsubscribeAllLabAdminUsers
    },
  },
})

